import { Eraser } from 'lucide-react'
import {
  forwardRef,
  type PointerEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { Button } from '@/components/ui/button'

/**
 * O canvas onde o cliente assina a vistoria.
 *
 * ---------------------------------------------------------------------------
 * O PNG É DOCUMENTO, NÃO INTERFACE — POR ISSO AS CORES SÃO FIXAS
 * ---------------------------------------------------------------------------
 * O bitmap exportado vai para o storage e aparece em contrato e disputa de
 * caução. Se as cores viessem do tema, a mesma assinatura sairia clara num
 * painel e escura no outro — prova visual que muda de cara conforme quem a
 * capturou. Os dois hexes abaixo espelham `--bg-elevated` e `--fg-primary` do
 * tema ESCURO (o padrão da marca) e são a única exceção consciente ao "nenhum
 * hex fora dos tokens": aqui eles pintam um documento, não a UI.
 *
 * ---------------------------------------------------------------------------
 * POINTER EVENTS, NÃO MOUSE + TOUCH SEPARADOS
 * ---------------------------------------------------------------------------
 * `pointerdown/move/up` cobrem mouse, dedo e caneta com UM conjunto de
 * handlers, e `setPointerCapture` mantém o traço vivo quando o dedo escorrega
 * para fora do canvas — soltar no meio da rubrica não a corta. `touch-none`
 * impede a página de rolar enquanto se assina no celular.
 *
 * ---------------------------------------------------------------------------
 * QUEM EXPORTA É A TELA, VIA HANDLE
 * ---------------------------------------------------------------------------
 * O componente não sobe nada: `exportarPng()` devolve o `Blob` (via
 * `canvas.toBlob`) e a tela decide quando enviá-lo — no submit, com cache até
 * o traço mudar. `aoMudar` dispara a CADA traço novo e no limpar, e é o gatilho
 * para a tela invalidar um upload já feito: sem isso, assinar de novo depois de
 * um submit falho reenviaria o PNG antigo.
 */

const LARGURA = 640
const ALTURA = 220
const ESPESSURA_DO_TRACO = 2.5

/** `--bg-elevated` do tema escuro — ver o cabeçalho: documento, não UI. */
const FUNDO_DA_PRANCHETA = '#141416'
/** `--border-strong` do tema escuro: a linha de base onde se apoia a rubrica. */
const LINHA_DE_BASE = '#3A3A40'
const COR_DO_TRACO = '#FFFFFF'

function pintarFundo(contexto: CanvasRenderingContext2D): void {
  contexto.fillStyle = FUNDO_DA_PRANCHETA
  contexto.fillRect(0, 0, LARGURA, ALTURA)

  contexto.strokeStyle = LINHA_DE_BASE
  contexto.lineWidth = 1
  contexto.beginPath()
  contexto.moveTo(40, ALTURA - 48)
  contexto.lineTo(LARGURA - 40, ALTURA - 48)
  contexto.stroke()

  contexto.lineWidth = ESPESSURA_DO_TRACO
  contexto.lineCap = 'round'
  contexto.lineJoin = 'round'
}

export interface AssinaturaCanvasHandle {
  /** O PNG da assinatura, ou `null` se ninguém traçou nada ainda. */
  readonly exportarPng: () => Promise<Blob | null>
  readonly limpar: () => void
}

export interface AssinaturaCanvasProps {
  /** Chamado com `true` a cada traço novo e com `false` no limpar. */
  readonly aoMudar?: ((possuiTraco: boolean) => void) | undefined
  readonly desabilitado?: boolean
}

export const AssinaturaCanvas = forwardRef<AssinaturaCanvasHandle, AssinaturaCanvasProps>(
  function AssinaturaCanvas({ aoMudar, desabilitado = false }, ref): ReactElement {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const desenhandoRef = useRef(false)
    const possuiTracoRef = useRef(false)
    const [possuiTraco, setPossuiTraco] = useState(false)

    // O bitmap nasce na densidade do monitor: sem o fator, a assinatura sai
    // serrilhada em tela retina — e serrilhado num documento parece adulterado.
    useEffect(() => {
      const canvas = canvasRef.current

      if (canvas === null) {
        return
      }

      const densidade = Math.max(1, Math.min(3, window.devicePixelRatio))
      canvas.width = Math.round(LARGURA * densidade)
      canvas.height = Math.round(ALTURA * densidade)

      const contexto = canvas.getContext('2d')

      if (contexto === null) {
        return
      }

      contexto.scale(densidade, densidade)
      pintarFundo(contexto)
    }, [])

    function contextoAtual(): CanvasRenderingContext2D | null {
      return canvasRef.current?.getContext('2d') ?? null
    }

    /** Posição do ponteiro em unidades LÓGICAS do canvas (o CSS o estica). */
    function posicaoDoPonteiro(evento: PointerEvent<HTMLCanvasElement>): {
      x: number
      y: number
    } | null {
      const retangulo = evento.currentTarget.getBoundingClientRect()

      if (retangulo.width === 0 || retangulo.height === 0) {
        return null
      }

      return {
        x: ((evento.clientX - retangulo.left) / retangulo.width) * LARGURA,
        y: ((evento.clientY - retangulo.top) / retangulo.height) * ALTURA,
      }
    }

    function aoPressionar(evento: PointerEvent<HTMLCanvasElement>): void {
      const contexto = contextoAtual()
      const ponto = posicaoDoPonteiro(evento)

      if (desabilitado || contexto === null || ponto === null) {
        return
      }

      evento.currentTarget.setPointerCapture(evento.pointerId)
      desenhandoRef.current = true

      contexto.strokeStyle = COR_DO_TRACO
      contexto.beginPath()
      contexto.moveTo(ponto.x, ponto.y)
      // Um toque sem arrasto também deixa marca — é o pingo do "i".
      contexto.lineTo(ponto.x + 0.1, ponto.y + 0.1)
      contexto.stroke()

      possuiTracoRef.current = true
      setPossuiTraco(true)
      // A cada traço, não só no primeiro: é o que invalida o PNG já enviado.
      aoMudar?.(true)
    }

    function aoMover(evento: PointerEvent<HTMLCanvasElement>): void {
      if (!desenhandoRef.current) {
        return
      }

      const contexto = contextoAtual()
      const ponto = posicaoDoPonteiro(evento)

      if (contexto === null || ponto === null) {
        return
      }

      contexto.lineTo(ponto.x, ponto.y)
      contexto.stroke()
    }

    function aoSoltar(evento: PointerEvent<HTMLCanvasElement>): void {
      desenhandoRef.current = false

      if (evento.currentTarget.hasPointerCapture(evento.pointerId)) {
        evento.currentTarget.releasePointerCapture(evento.pointerId)
      }
    }

    // `useCallback` porque a função entra nas dependências do handle abaixo —
    // recriá-la a cada render recriaria o handle sem motivo.
    const limparTudo = useCallback((): void => {
      const contexto = canvasRef.current?.getContext('2d') ?? null

      if (contexto !== null) {
        pintarFundo(contexto)
      }

      desenhandoRef.current = false
      possuiTracoRef.current = false
      setPossuiTraco(false)
      aoMudar?.(false)
    }, [aoMudar])

    useImperativeHandle(
      ref,
      () => ({
        exportarPng: () =>
          new Promise<Blob | null>((resolver) => {
            const canvas = canvasRef.current

            if (canvas === null || !possuiTracoRef.current) {
              resolver(null)
              return
            }

            canvas.toBlob((blob) => {
              resolver(blob)
            }, 'image/png')
          }),
        limpar: limparTudo,
      }),
      [limparTudo],
    )

    return (
      <div className="space-y-2">
        <canvas
          ref={canvasRef}
          aria-label="Área de assinatura do cliente — assine com o dedo ou com o mouse"
          className="h-auto w-full touch-none rounded-lg border border-border"
          style={{ aspectRatio: `${String(LARGURA)} / ${String(ALTURA)}` }}
          onPointerDown={aoPressionar}
          onPointerMove={aoMover}
          onPointerUp={aoSoltar}
          onPointerCancel={aoSoltar}
        />

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-fg-muted">Assine com o dedo ou com o mouse.</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={limparTudo}
            disabled={desabilitado || !possuiTraco}
          >
            <Eraser aria-hidden="true" />
            Limpar
          </Button>
        </div>
      </div>
    )
  },
)
